import { useEffect, useRef, useState, type ComponentType, type MouseEvent, type ReactNode, type SVGProps } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { IconLock, IconPrinter } from '../layout/icons';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface BillActionTileBaseProps {
  icon: IconType;
  label: string;
  classPrefix?: string;
  active?: boolean;
  soon?: boolean;
  title?: string;
}

type BillActionTileProps = BillActionTileBaseProps & ({ to: string; onClick?: never } | { to?: undefined; onClick: (event: MouseEvent<HTMLButtonElement>) => void });

/** A single option tile in a bill/admission detail page's action grid (e.g. "Bill Details", "Print Bill"). */
export const BillActionTile = ({ icon: Icon, label, classPrefix = 'opd-bill', active, soon, title, to, onClick }: BillActionTileProps) => {
  const className = `${classPrefix}-action-tile${active ? ' active' : ''}${soon ? ' soon' : ''}`;
  const content = (
    <>
      <Icon />
      <span>{label}</span>
      {soon ? <span className={`${classPrefix}-soon-pill`}><IconLock />Soon</span> : null}
    </>
  );
  if (to) return <Link className={className} to={to} title={title}>{content}</Link>;
  return <button type="button" className={className} title={title} onClick={onClick}>{content}</button>;
};

interface BillActionGridProps {
  classPrefix?: string;
  printHiddenClass?: string;
  children: ReactNode;
}

/** Container grid for BillActionTile options, shown in a detail page's sidebar. */
export const BillActionGrid = ({ classPrefix = 'opd-bill', printHiddenClass = 'opd-print-hidden', children }: BillActionGridProps) => (
  <div className={`${classPrefix}-action-grid ${printHiddenClass}`}>{children}</div>
);

interface BillActionHoverTileProps {
  icon: IconType;
  label: string;
  classPrefix?: string;
  items: Array<{ label: string; onClick: () => void }>;
}

const HOVER_CLOSE_DELAY_MS = 150;
const MENU_WIDTH_PX = 240;
const VIEWPORT_PADDING_PX = 12;

/** An option tile that reveals a small print-options menu below it on hover (or keyboard focus). */
export const BillActionHoverTile = ({ icon: Icon, label, classPrefix = 'opd-bill', items }: BillActionHoverTileProps) => {
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openMenu = () => {
    clearCloseTimer();
    const rect = tileRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxLeft = window.innerWidth - MENU_WIDTH_PX - VIEWPORT_PADDING_PX;
    const left = Math.max(VIEWPORT_PADDING_PX, Math.min(rect.left, maxLeft));
    setMenuPosition({ top: rect.bottom + 6, left });
  };
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setMenuPosition(null), HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => clearCloseTimer, []);

  return (
    <>
      <div
        ref={tileRef}
        className={`${classPrefix}-action-tile${menuPosition ? ' active' : ''}`}
        tabIndex={0}
        role="button"
        aria-haspopup="menu"
        aria-expanded={menuPosition !== null}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onFocus={openMenu}
        onBlur={scheduleClose}
        onKeyDown={(event) => { if (event.key === 'Escape') setMenuPosition(null); }}
      >
        <Icon /><span>{label}</span>
      </div>
      {menuPosition ? createPortal(
        <div
          className="opd-bill-tile-dropdown"
          style={{ top: menuPosition.top, left: menuPosition.left }}
          role="menu"
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          onFocus={clearCloseTimer}
          onBlur={scheduleClose}
        >
          {items.map((item) => (
            <button key={item.label} type="button" role="menuitem" onClick={() => { setMenuPosition(null); item.onClick(); }}>
              <IconPrinter />{item.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  );
};
